import { useEffect, useState } from 'react'
import type { Tour } from '../lib/models'
import { SPORTS } from '../lib/models'
import { formatDuration, formatKm } from '../lib/geo'
import { profileFromCoords } from '../lib/elevation'
import { toGpx } from '../lib/gpx'
import { useTours } from '../state/tours'
import { useNav } from '../state/nav'
import { usePlanner } from '../state/planner'
import { useUi } from '../state/ui'
import { fetchForecast, weatherInfo, type DailyForecast } from '../lib/weather'
import { deleteTilesForTour, downloadTilesForTour, type DownloadProgress } from '../lib/offline'
import { tileCountForTour } from '../lib/db'
import { MAP_STYLE_URL } from '../lib/providers'
import ElevationChart from './ElevationChart'
import SurfaceBars from './SurfaceBars'
import { downloadText } from './util'
import { SPORT_ICONS } from '../App'
import { IconBack, IconDownload, IconGpx, IconNavArrow, IconTrash, IconCheck } from './icons'

export default function TourDetail({ tour }: { tour: Tour }) {
  const { open, remove } = useTours()
  const showToast = useUi((s) => s.showToast)
  const [forecast, setForecast] = useState<DailyForecast[]>([])
  const [dl, setDl] = useState<DownloadProgress | null>(null)
  const [offlineTiles, setOfflineTiles] = useState(0)
  const [confirmDelete, setConfirmDelete] = useState(false)

  const profile = profileFromCoords(tour.data.coords)
  const start = tour.data.coords[0]
  const Icon = SPORT_ICONS[tour.sport] ?? SPORT_ICONS.hike

  useEffect(() => {
    void fetchForecast(start[1], start[0])
      .then(setForecast)
      .catch(() => {})
    void tileCountForTour(tour.id).then(setOfflineTiles)
  }, [tour.id, start])

  const navigate = () => {
    useNav.getState().start(tour.name, tour.data.coords, tour.data.instructions ?? [], tour.sport)
    useUi.getState().setSheetOpen(false)
  }

  const editInPlanner = () => {
    const wps =
      tour.data.waypoints && tour.data.waypoints.length >= 2
        ? tour.data.waypoints
        : [
            { lng: tour.data.coords[0][0], lat: tour.data.coords[0][1] },
            {
              lng: tour.data.coords[tour.data.coords.length - 1][0],
              lat: tour.data.coords[tour.data.coords.length - 1][1],
            },
          ]
    usePlanner.getState().setWaypoints(wps, tour.sport)
    useUi.getState().setPanel('plan')
    open(null)
  }

  const exportGpx = () =>
    downloadText(
      toGpx({
        name: tour.name,
        coords: tour.data.coords,
        times: tour.data.times,
        waypoints: tour.data.waypoints?.map((w, i) => ({ name: w.name ?? `Waypoint ${i + 1}`, lng: w.lng, lat: w.lat })),
      }),
      `${tour.name.replace(/[^\w\d-]+/g, '-').toLowerCase()}.gpx`,
      'application/gpx+xml',
    )

  const downloadOffline = async () => {
    setDl({ done: 0, total: 1, bytes: 0 })
    try {
      const result = await downloadTilesForTour(tour.id, tour.data.coords, MAP_STYLE_URL, setDl)
      setOfflineTiles(result.total)
      showToast(`Offline map saved (${(result.bytes / 1e6).toFixed(1)} MB)`)
    } catch {
      showToast('Offline download failed — check your connection')
    } finally {
      setDl(null)
    }
  }

  const s = tour.data.stats
  return (
    <>
      <div className="row" style={{ marginBottom: 10 }}>
        <button className="icon-btn" onClick={() => open(null)} aria-label="Back">
          <IconBack />
        </button>
        <span className="spacer" />
        <span className={`badge${tour.kind === 'recorded' ? ' rec' : ''}`}>{tour.kind}</span>
      </div>

      <div className="row" style={{ gap: 12, marginBottom: 4 }}>
        <span
          className="t-ico"
          style={{
            width: 42,
            height: 42,
            borderRadius: 15,
            background: 'var(--accent-tint)',
            color: 'var(--accent-deep)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          <Icon width={22} height={22} />
        </span>
        <div style={{ minWidth: 0 }}>
          <h2 style={{ margin: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{tour.name}</h2>
          <span className="muted">
            {SPORTS.find((sp) => sp.id === tour.sport)?.label} ·{' '}
            {new Date(tour.createdAt).toLocaleDateString(undefined, { day: 'numeric', month: 'long', year: 'numeric' })}
          </span>
        </div>
      </div>

      <div className="shell">
        <div className="core">
          <div className="stats">
            <div className="stat">
              <div className="v">{formatKm(s.distanceM)}</div>
              <div className="l">Distance</div>
            </div>
            <div className="stat">
              <div className="v">{s.durationS ? formatDuration(s.durationS) : '—'}</div>
              <div className="l">{tour.kind === 'recorded' ? 'Time' : 'Est. time'}</div>
            </div>
            <div className="stat">
              <div className="v">{s.ascentM} m</div>
              <div className="l">Ascent</div>
            </div>
            <div className="stat">
              <div className="v">{s.descentM} m</div>
              <div className="l">Descent</div>
            </div>
          </div>
          {profile && <ElevationChart profile={profile} />}
        </div>
      </div>

      <div className="row" style={{ marginBottom: 8 }}>
        <button className="btn primary" style={{ flex: 1 }} onClick={navigate}>
          Navigate
          <span className="isl">
            <IconNavArrow />
          </span>
        </button>
        <button className="btn" onClick={editInPlanner}>
          Edit route
        </button>
      </div>
      <div className="row">
        <button className="btn sm" style={{ flex: 1 }} onClick={exportGpx}>
          <IconGpx /> GPX export
        </button>
      </div>

      {(tour.data.waytypes || tour.data.surfaces) && (
        <div className="shell">
          <div className="core">
            {tour.data.waytypes && <SurfaceBars title="Way types" segments={tour.data.waytypes} />}
            {tour.data.surfaces && <SurfaceBars title="Surfaces" segments={tour.data.surfaces} />}
          </div>
        </div>
      )}

      <span className="eyebrow">Offline map</span>
      {dl ? (
        <p className="muted" style={{ margin: '4px 2px' }}>
          <span className="busy-dot" /> Downloading… {dl.done}/{dl.total} tiles ({(dl.bytes / 1e6).toFixed(1)} MB)
        </p>
      ) : offlineTiles > 0 ? (
        <div className="hint tinted">
          <div className="row">
            <IconCheck style={{ flexShrink: 0 }} />
            <span style={{ flex: 1 }}>{offlineTiles} map tiles stored — this tour works without coverage.</span>
            <button
              className="chip danger"
              onClick={() => {
                void deleteTilesForTour(tour.id).then(() => setOfflineTiles(0))
              }}
            >
              Remove
            </button>
          </div>
        </div>
      ) : (
        <button className="btn block" onClick={() => void downloadOffline()}>
          <IconDownload /> Save map for offline use
        </button>
      )}

      {forecast.length > 0 && (
        <>
          <span className="eyebrow">Weather at start</span>
          <div className="weather-strip">
            {forecast.map((d) => {
              const w = weatherInfo(d.code)
              return (
                <div key={d.date} className="weather-day" title={w.label}>
                  <div>{new Date(d.date).toLocaleDateString(undefined, { weekday: 'short' })}</div>
                  <div className="w-icon">{w.icon}</div>
                  <div className="w-temp">
                    {d.tMax}° <span className="muted">{d.tMin}°</span>
                  </div>
                  <div className="muted">{d.precipProb}%</div>
                </div>
              )
            })}
          </div>
        </>
      )}

      <div style={{ marginTop: 18 }}>
        {confirmDelete ? (
          <div className="row">
            <button
              className="btn danger"
              style={{ flex: 1 }}
              onClick={() => {
                void deleteTilesForTour(tour.id)
                void remove(tour.id)
              }}
            >
              <IconTrash /> Really delete
            </button>
            <button className="btn" onClick={() => setConfirmDelete(false)}>
              Keep it
            </button>
          </div>
        ) : (
          <button className="btn sm danger" onClick={() => setConfirmDelete(true)}>
            <IconTrash /> Delete tour
          </button>
        )}
      </div>
    </>
  )
}
